import type { ComputedRef } from 'vue'

export const PLATFORM_TABLE_SCROLL_FALLBACK_PX = 240
/**
 * Swing `<height>Nr</height>` (TableHeight.BY_ROW): N visible body rows.
 * Matches typical MTreeTable/MTable row min-height.
 */
export const PLATFORM_TABLE_ROW_HEIGHT_PX = 40
/**
 * Leftover `--fill` must not shrink below this many body rows (form 2717).
 * Swing AUTO uses TableHeight.MINIMUM (50px viewport); user-visible floor is 2 rows.
 */
export const PLATFORM_TABLE_FILL_MIN_BODY_ROWS = 2
/** Sticky thead in the fill body wrap (same row rhythm as body cells). */
export const PLATFORM_TABLE_THEAD_HEIGHT_PX = PLATFORM_TABLE_ROW_HEIGHT_PX
export const PLATFORM_TABLE_FILL_MIN_BODY_PX =
  PLATFORM_TABLE_FILL_MIN_BODY_ROWS * PLATFORM_TABLE_ROW_HEIGHT_PX + PLATFORM_TABLE_THEAD_HEIGHT_PX
/**
 * Document forms (не dialog): таблица с auto height / без grabVertical
 * занимает оставшуюся высоту окна, а не фиксированные 240px.
 */
export const PLATFORM_TABLE_SCROLL_FALLBACK_CSS = `max(${PLATFORM_TABLE_SCROLL_FALLBACK_PX}px, calc(100dvh - 22rem))`
/**
 * Compact dialog без grabVertical: раньше 180px → ~1 строка (форма 2730).
 * Viewport-tied body, без flex (flex без stretch-ряда схлопывается).
 */
export const PLATFORM_DIALOG_COMPACT_TABLE_SCROLL_CSS = 'max(360px, min(58dvh, 720px))'
export const PLATFORM_DIALOG_COMPACT_TREETABLE_SCROLL_CSS = 'max(380px, min(58dvh, 760px))'
/** Numeric floor kept for callers/tests that still import px constants. */
export const PLATFORM_DIALOG_COMPACT_TABLE_SCROLL_PX = 360
export const PLATFORM_DIALOG_COMPACT_TREETABLE_SCROLL_PX = 380

export type PlatformDialogLayoutLike = {
  compact?: boolean
  sized?: boolean
} | null | undefined

export type ResolveTableScrollHeightInput = {
  element?: {
    type?: { _text?: string }
    height?: { _text?: string }
    grabVertical?: { _text?: string }
  }
  dialogLayout?: PlatformDialogLayoutLike | ComputedRef<PlatformDialogLayoutLike>
  /**
   * Form `<footer>` with pages (Swing SOUTH). Compact+sized leftover uses flex
   * so the table fills remaining main (form 1084), not a fixed 58dvh body.
   */
  hasFormFooter?: boolean
}

/** Parsed form has a non-empty footer region (tabs after getTabs). */
export function formLayoutHasFooter(form: unknown): boolean {
  if (!form || typeof form !== 'object') return false
  const root = form as Record<string, unknown>
  if (Array.isArray(root.footer) && root.footer.length > 0) return true
  const inner = root.form
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    const nested = inner as Record<string, unknown>
    return Array.isArray(nested.footer) && nested.footer.length > 0
  }
  return false
}

const readDialogLayout = (
  dialogLayout: ResolveTableScrollHeightInput['dialogLayout'],
): PlatformDialogLayoutLike => {
  if (dialogLayout == null) return null
  if (typeof dialogLayout === 'object' && 'value' in dialogLayout) {
    return dialogLayout.value ?? null
  }
  return dialogLayout
}

const getElementType = (element: ResolveTableScrollHeightInput['element']): string => {
  const raw = element?.type as { _text?: unknown; value?: unknown } | string | undefined
  if (typeof raw === 'string') return raw.trim().toUpperCase()
  const text = raw?._text ?? raw?.value ?? ''
  return String(text).trim().toUpperCase()
}

const isCompactDialogContentScrollType = (type: string): boolean =>
  type === 'TREETABLE' || type === 'EDITABLE'

const compactDialogScrollHeight = (
  element: ResolveTableScrollHeightInput['element'],
): string | null => {
  const type = getElementType(element)
  if (type === 'TREETABLE') {
    return PLATFORM_DIALOG_COMPACT_TREETABLE_SCROLL_CSS
  }
  // XML `EdiTable` uppercases to EDITABLE.
  if (type === 'TABLE' || type === 'LISTBOXTABLE' || type === 'EDITABLE') {
    return PLATFORM_DIALOG_COMPACT_TABLE_SCROLL_CSS
  }
  return null
}

/**
 * TABLE/TREETABLE scroll mode:
 * - grabVertical → flex
 * - compact dialog + form footer, no grabVertical → flex leftover of main (1084).
 *   Viewport 58dvh is a fixed body inside leftover wrapper → clipped gap.
 * - compact dialog without grabVertical → viewport-tied CSS (2730)
 * - sized non-compact → flex
 * - document TreeTable auto → flex leftover
 * - compact dialog without footer: TreeTable/EdiTable hug content + dialog scroll (2673).
 *   Do not fall through to sized→flex: `--fill` on 4372 clips the window and hides
 *   the panel under a content-sized tree in 4400. Table keeps 58dvh (2730).
 * - else document Table fallback CSS (2565)
 */
export function resolveTableScrollHeight(input: ResolveTableScrollHeightInput): string | null {
  const element = input.element
  const heightText = String(element?.height?._text ?? '').trim()
  if (heightText && heightText !== 'auto' && heightText !== '0') {
    if (heightText.endsWith('%')) {
      return heightText
    }
    // Swing TableHeight.BY_ROW: "20r" = 20 rows (must run before parseInt("20r") → 20px).
    if (/^\d+[rR]$/.test(heightText)) {
      const rows = Number.parseInt(heightText.slice(0, -1), 10)
      if (Number.isFinite(rows) && rows > 0) {
        return `${rows * PLATFORM_TABLE_ROW_HEIGHT_PX}px`
      }
    } else {
      const parsed = Number.parseInt(heightText, 10)
      if (Number.isFinite(parsed) && parsed > 0) {
        return `${parsed}px`
      }
    }
  }

  const grabVertical = String(element?.grabVertical?._text ?? '').trim().toLowerCase()
  const hasGrabVertical = grabVertical === 'true' || grabVertical === '1'
  const dialogLayout = readDialogLayout(input.dialogLayout)

  if (hasGrabVertical) {
    return 'flex'
  }

  // Platform dialogs always mark compact. sized+flex without grabVertical collapses
  // (Block grows only grabVertical rows) — Table uses CSS body height (2730).
  // Exception: form footer SOUTH — leftover flex so the table fills remaining main (1084).
  // TreeTable without footer: Swing page JScrollPane (form grabVertical=false).
  // 58dvh is viewport-sized and taller than a sized dialog body → clips sibling
  // panels (form 2673: 4400 overflows, 4372 gone). Content + dialog scroll instead.
  if (dialogLayout?.compact) {
    const type = getElementType(element)
    const compactHeight = compactDialogScrollHeight(element)
    if (input.hasFormFooter && dialogLayout.sized && compactHeight) {
      return 'flex'
    }
    // Must return here: empty compactHeight used to fall through to sized→flex
    // (XML `EdiTable` before uppercase; 2673 purposeCategoryList `--fill`).
    if (isCompactDialogContentScrollType(type)) {
      return null
    }
    if (compactHeight) {
      return compactHeight
    }
  }

  if (dialogLayout?.sized) {
    return 'flex'
  }

  // Document TreeTable + height=auto: Swing TableHeight.AUTO → wantGrowVertically.
  // Viewport leftover clips .form-main-body; without --fill MTreeTable has
  // overflow:hidden and no inner bar (form 2673). Table documents keep the
  // CSS fallback (2565 listOrders). Compact without footer stays 58dvh (2730).
  if (getElementType(element) === 'TREETABLE') {
    return 'flex'
  }

  // Вне диалога: не режем реестры в 240px (форма 2565 listOrders и аналоги).
  return PLATFORM_TABLE_SCROLL_FALLBACK_CSS
}

export function resolveTreeTableFillAvailable(input: ResolveTableScrollHeightInput): boolean {
  return resolveTableScrollHeight(input) === 'flex'
}
